import { useState, useEffect, useRef } from 'react'
import { X, Zap, Check, Sparkles } from 'lucide-react'

export default function UpgradeModal({ isOpen, onClose, onSuccess, isAnnual = false }) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [billingCycle, setBillingCycle] = useState(isAnnual ? 'annual' : 'monthly')
    const paypalRef = useRef(null)
    const paypalButtonRendered = useRef(false)

    // PayPal configuration from environment or defaults
    const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || 'AWSMXRZZXM9nglBELZmZfgQ_dd0Go3hSKKgNZ5wfjCAlYTGgFwJIJQn2Hd_TH0_2sScDXENteWVpxESU'

    // Separate plan IDs for monthly and annual subscriptions
    const PAYPAL_PLAN_ID_MONTHLY = import.meta.env.VITE_PAYPAL_PLAN_ID_MONTHLY || import.meta.env.VITE_PAYPAL_PLAN_ID || 'P-21G26709CS318545FNH4WY7I'
    const PAYPAL_PLAN_ID_ANNUAL = import.meta.env.VITE_PAYPAL_PLAN_ID_ANNUAL || 'P-48F36853NL162382SNH4W2RQ'

    // Select plan based on billing cycle
    const activePlanId = billingCycle === 'annual' && PAYPAL_PLAN_ID_ANNUAL
        ? PAYPAL_PLAN_ID_ANNUAL
        : PAYPAL_PLAN_ID_MONTHLY

    // Pricing based on billing cycle
    const pricing = {
        monthly: { price: 3, period: 'month', label: '/month' },
        annual: { price: 1.50, period: 'year', label: '/month', totalPrice: 18, savings: '50%' }
    }
    const currentPricing = pricing[billingCycle]

    const features = [
        'Unlimited AI-powered suggestions',
        'Advanced cliché detection',
        'Custom style guides',
        'Priority support',
        'Chrome extension Pro features',
        'Detailed analytics & reports',
    ]

    // Update billing cycle when isAnnual prop changes
    useEffect(() => {
        setBillingCycle(isAnnual ? 'annual' : 'monthly')
    }, [isAnnual])

    useEffect(() => {
        if (!isOpen) return

        // Reset states when modal opens
        setLoading(true)
        setError(null)
        paypalButtonRendered.current = false

        // Load PayPal SDK
        const loadPayPalScript = () => {
            // Check if script already exists
            const existingScript = document.getElementById('paypal-sdk')
            if (existingScript) {
                renderPayPalButton()
                return
            }

            const script = document.createElement('script')
            script.id = 'paypal-sdk'
            script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription&components=buttons`
            script.setAttribute('data-sdk-integration-source', 'button-factory')

            script.onload = () => {
                renderPayPalButton()
            }

            script.onerror = () => {
                setError('Failed to load PayPal. Please try again.')
                setLoading(false)
            }

            document.body.appendChild(script)
        }

        const renderPayPalButton = () => {
            if (!paypalRef.current) return
            if (!window.paypal) {
                setTimeout(renderPayPalButton, 100)
                return
            }

            // Clear previous button
            paypalRef.current.innerHTML = ''
            paypalButtonRendered.current = false

            try {
                window.paypal.Buttons({
                    style: {
                        shape: 'pill',
                        color: 'blue',
                        layout: 'vertical',
                        label: 'subscribe'
                    },
                    createSubscription: function (data, actions) {
                        return actions.subscription.create({
                            plan_id: activePlanId
                        })
                    },
                    onApprove: function (data, actions) {
                        console.log('Subscription approved:', data.subscriptionID, 'Billing:', billingCycle)
                        // Handle successful subscription
                        if (onSuccess) {
                            onSuccess(data.subscriptionID, billingCycle)
                        }
                        onClose()
                        // Show success message
                        alert('🎉 Welcome to InkSpot Pro! Your subscription is now active.')
                        // Reload to update user status
                        window.location.reload()
                    },
                    onError: function (err) {
                        console.error('PayPal error:', err)
                        setError('Payment failed. Please try again.')
                    }
                }).render(paypalRef.current)

                paypalButtonRendered.current = true
                setLoading(false)
            } catch (err) {
                console.error('Error rendering PayPal button:', err)
                setError('Failed to initialize PayPal. Please try again.')
                setLoading(false)
            }
        }

        // Small delay to ensure DOM is ready
        setTimeout(loadPayPalScript, 100)

        return () => {
            // Cleanup
            paypalButtonRendered.current = false
        }
    }, [isOpen, PAYPAL_CLIENT_ID, activePlanId, billingCycle, onClose, onSuccess])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg glass-card p-8 animate-scale-in max-h-[90vh] overflow-y-auto">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        Upgrade to Pro
                    </h2>
                    <p className="text-white/60">
                        Unlock your full writing potential
                    </p>
                </div>

                {/* Billing Toggle */}
                <div className="flex items-center justify-center gap-3 mb-4">
                    <span className={`text-sm font-medium transition-colors ${billingCycle === 'monthly' ? 'text-white' : 'text-white/50'}`}>
                        Monthly
                    </span>
                    <button
                        onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                        className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500
                            ${billingCycle === 'annual' ? 'bg-gradient-to-r from-primary-500 to-accent-500' : 'bg-white/20'}`}
                        role="switch"
                        aria-checked={billingCycle === 'annual'}
                    >
                        <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-lg transition-all duration-300
                                ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-0'}`}
                        />
                    </button>
                    <span className={`text-sm font-medium transition-colors ${billingCycle === 'annual' ? 'text-white' : 'text-white/50'}`}>
                        Annual
                        <span className="ml-1.5 text-xs text-primary-400 font-semibold bg-primary-500/10 px-1.5 py-0.5 rounded-full">
                            Save 50%
                        </span>
                    </span>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-2">
                        <span className="text-4xl font-bold text-white">${currentPricing.price}</span>
                        <span className="text-white/50">{currentPricing.label}</span>
                    </div>
                    {billingCycle === 'annual' && (
                        <p className="text-sm text-primary-400 mt-1">
                            Billed annually (${currentPricing.totalPrice}/year)
                        </p>
                    )}
                    <p className="text-sm text-white/50 mt-1">
                        Cancel anytime
                    </p>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                    {features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                                <Check className="w-3 h-3 text-green-400" />
                            </div>
                            <span className="text-white/80">{feature}</span>
                        </div>
                    ))}
                </div>

                {/* PayPal Button Container */}
                <div className="min-h-[50px]">
                    {loading && (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-white/60">Loading payment options...</span>
                        </div>
                    )}

                    {error && (
                        <div className="text-center py-4">
                            <p className="text-red-400 mb-2">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="text-primary-400 hover:underline"
                            >
                                Refresh page
                            </button>
                        </div>
                    )}

                    <div ref={paypalRef} className={loading ? 'hidden' : ''} />
                </div>

                {/* Security Note */}
                <p className="text-center text-xs text-white/40 mt-4">
                    Secure payment powered by PayPal
                </p>
            </div>
        </div>
    )
}
